import {
    Box,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Card,
    CardBody,
    Flex,
    Badge,
    Text,
    Spinner,
    Center,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Select,
    useToast,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdAdd, MdMoreVert, MdVisibility, MdDelete, MdEdit } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import { useProjects, useCreateProject, useDeleteProject } from '@/services/hooks';

const statusColors: Record<string, string> = {
    draft: 'gray',
    active: 'green',
    archived: 'yellow',
    completed: 'blue',
};

export function Projects() {
    const navigate = useNavigate();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [page, setPage] = useState(1);

    const { data, isLoading } = useProjects({ page, pageSize: 20 });
    const createMutation = useCreateProject();
    const deleteMutation = useDeleteProject();

    const [newProject, setNewProject] = useState({
        title: '',
        agency: '',
        contract_id: '',
        description: '',
    });

    const handleCreate = async () => {
        try {
            await createMutation.mutateAsync(newProject);
            toast({
                title: 'Project created',
                status: 'success',
                duration: 3000,
            });
            onClose();
            setNewProject({ title: '', agency: '', contract_id: '', description: '' });
        } catch (error) {
            toast({
                title: 'Failed to create project',
                status: 'error',
                duration: 3000,
            });
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm('Are you sure you want to delete this project?')) return;

        try {
            await deleteMutation.mutateAsync(id);
            toast({
                title: 'Project deleted',
                status: 'success',
                duration: 3000,
            });
        } catch (error) {
            toast({
                title: 'Failed to delete project',
                status: 'error',
                duration: 3000,
            });
        }
    };

    const formatDate = (date: string) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatCurrency = (amount: number | null) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={6}>
                <Heading size="lg">Projects</Heading>
                <Button leftIcon={<MdAdd />} colorScheme="brand" onClick={onOpen}>
                    New Project
                </Button>
            </Flex>

            <Card>
                <CardBody p={0}>
                    {isLoading ? (
                        <Center py={12}>
                            <Spinner size="lg" color="brand.500" />
                        </Center>
                    ) : !data?.items?.length ? (
                        <Center py={12}>
                            <Box textAlign="center">
                                <Text color="gray.500" mb={4}>No projects found</Text>
                                <Button leftIcon={<MdAdd />} onClick={onOpen}>
                                    Create your first project
                                </Button>
                            </Box>
                        </Center>
                    ) : (
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Project</Th>
                                    <Th>Agency</Th>
                                    <Th>Contract ID</Th>
                                    <Th>Award Date</Th>
                                    <Th isNumeric>Award Amount</Th>
                                    <Th>Status</Th>
                                    <Th w="50px"></Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {data.items.map((project: any) => (
                                    <Tr
                                        key={project.id}
                                        cursor="pointer"
                                        _hover={{ bg: 'navy.700' }}
                                        onClick={() => navigate(`/projects/${project.id}`)}
                                    >
                                        <Td>
                                            <Text fontWeight="medium" noOfLines={1} maxW="300px">
                                                {project.title}
                                            </Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">{project.agency}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm" fontFamily="mono">
                                                {project.contract_id || '-'}
                                            </Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">{formatDate(project.award_date)}</Text>
                                        </Td>
                                        <Td isNumeric>
                                            <Text fontSize="sm" fontWeight="medium">
                                                {formatCurrency(project.award_amount)}
                                            </Text>
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={statusColors[project.status] || 'gray'}>
                                                {project.status}
                                            </Badge>
                                        </Td>
                                        <Td onClick={(e) => e.stopPropagation()}>
                                            <Menu>
                                                <MenuButton
                                                    as={IconButton}
                                                    icon={<MdMoreVert />}
                                                    variant="ghost"
                                                    size="sm"
                                                />
                                                <MenuList>
                                                    <MenuItem icon={<MdVisibility />} onClick={() => navigate(`/projects/${project.id}`)}>
                                                        View Details
                                                    </MenuItem>
                                                    <MenuItem icon={<MdEdit />}>Edit</MenuItem>
                                                    <MenuItem
                                                        icon={<MdDelete />}
                                                        color="red.400"
                                                        onClick={() => handleDelete(project.id)}
                                                    >
                                                        Delete
                                                    </MenuItem>
                                                </MenuList>
                                            </Menu>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Pagination */}
            {data?.total > 20 && (
                <Flex justify="center" mt={4} gap={2}>
                    <Button
                        size="sm"
                        variant="ghost"
                        disabled={page === 1}
                        onClick={() => setPage((p) => p - 1)}
                    >
                        Previous
                    </Button>
                    <Text alignSelf="center" color="gray.400">
                        Page {page} of {Math.ceil(data.total / 20)}
                    </Text>
                    <Button
                        size="sm"
                        variant="ghost"
                        disabled={page >= Math.ceil(data.total / 20)}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Next
                    </Button>
                </Flex>
            )}

            {/* Create Project Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Create New Project</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Project Title</FormLabel>
                            <Input
                                placeholder="e.g., 2026 Highway Resurfacing Program"
                                value={newProject.title}
                                onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                            />
                        </FormControl>

                        <FormControl mb={4} isRequired>
                            <FormLabel>Agency</FormLabel>
                            <Select
                                placeholder="Select agency"
                                value={newProject.agency}
                                onChange={(e) => setNewProject({ ...newProject, agency: e.target.value })}
                            >
                                <option value="Chicago DPS">Chicago DPS</option>
                                <option value="Cook County">Cook County</option>
                                <option value="Illinois State">Illinois State</option>
                                <option value="Other">Other</option>
                            </Select>
                        </FormControl>

                        <FormControl mb={4}>
                            <FormLabel>Contract ID</FormLabel>
                            <Input
                                placeholder="e.g., C-2026-001"
                                value={newProject.contract_id}
                                onChange={(e) => setNewProject({ ...newProject, contract_id: e.target.value })}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                                placeholder="Project description..."
                                value={newProject.description}
                                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleCreate}
                            isLoading={createMutation.isPending}
                            isDisabled={!newProject.title || !newProject.agency}
                        >
                            Create Project
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
